<template>
  <div class="mfa-recovery">
    <div class="page-header">
      <div>
        <p class="eyebrow">SECURITY</p>
        <h1>2要素認証の登録状況</h1>
        <p>
          誰が2要素認証を登録しているかを確認し、
          <b>端末を失くした社員の登録を外して、やり直してもらえます</b>。
        </p>
      </div>
    </div>

    <CAlert
      v-if="message"
      color="success"
      close-button
      @update:show="message = ''"
    >{{ message }}</CAlert>

    <CAlert
      v-if="errorMessage"
      color="danger"
      close-button
      @update:show="errorMessage = ''"
    >{{ errorMessage }}</CAlert>

    <div v-if="loading" class="mfa-recovery__loading">
      <CSpinner color="primary"/>
      <span>登録状況を読み込んでいます。</span>
    </div>

    <template v-else>
      <!--
        まず数を見せる。

        **強制を始める前に、何人が止まるかを知る。**Google は展開手順の
        中に「登録状況を追う」を1ステップとして持っているし、GitHub は
        必須化の前に確認できるようにしてある。数を知らずに強制すると、
        当日に何人止まるか分からない。
      -->
      <!--
        **いま何が起きているかを先に言う。**

        2026-08-27 まで、この画面は「必須にすると〜」という仮定の文しか
        出していなかった。開いた人は、フラグを立てたのに何も変わらないのを見て
        「いま必須なのかどうかが分からない」と言った。当然で、書いていない。
      -->
      <CCard v-if="stage" class="mfa-recovery__stage-card">
        <CCardBody>
          <p :class="['mfa-recovery__stage', stageClass]">
            {{ stageHeadline }}
          </p>
          <p class="mfa-recovery__stage-detail">{{ stageDetail }}</p>
        </CCardBody>
      </CCard>

      <CCard class="mfa-recovery__summary">
        <CCardBody>
          <div class="mfa-recovery__counts">
            <div>
              <span class="mfa-recovery__count">{{ status.total }}</span>
              <span class="mfa-recovery__label">利用者</span>
            </div>
            <div :class="{ 'mfa-recovery__warn': status.notEnrolled > 0 }">
              <span class="mfa-recovery__count">{{ status.notEnrolled }}</span>
              <span class="mfa-recovery__label">まだ登録していない</span>
            </div>
          </div>
          <!--
            **止まるのは管理者だけ。**必須にしても、一般・担当者は
            今までどおり入れる（AdminMfaEnforcement.stageFor が管理者以外を
            必ず NONE で返す）。

            ここで全員を数えていた。25人中24人が未登録の会社で「24人」と
            出たが、実際に止まるのは4人だった。**6倍の数字を見せられたら、
            誰も必須にしない。**
          -->
          <p v-if="status.adminsNotEnrolled > 0" class="mfa-recovery__note">
            管理者の2要素認証を必須にすると、
            <b>この{{ status.adminsNotEnrolled }}人の管理者が、
            次のログインで登録を求められます</b>。
            <span v-if="othersNotEnrolled > 0" class="mfa-recovery__aside">
              （ほかに{{ othersNotEnrolled }}人が未登録ですが、管理者では
              ないので求められません）
            </span>
          </p>
          <p v-else-if="status.notEnrolled > 0" class="mfa-recovery__note">
            <b>管理者は全員が登録済みです。</b>
            必須にしても、いま止まる人はいません。
            （管理者以外に{{ status.notEnrolled }}人の未登録がいますが、
            求められません）
          </p>
          <p v-else class="mfa-recovery__note">
            全員が登録済みです。
          </p>
        </CCardBody>
      </CCard>

      <CCard>
        <CCardHeader>
          <strong>利用者ごとの状況</strong>
          <p>まだ登録していない人が上に出ます。</p>
        </CCardHeader>

        <CCardBody>
          <p v-if="!status.users.length" class="mfa-recovery__empty">
            利用者がいません。
          </p>

          <ul v-else class="mfa-recovery__list">
            <li
              v-for="user in status.users"
              :key="user.userId"
              class="mfa-recovery__user"
            >
              <div class="mfa-recovery__body">
                <p class="mfa-recovery__name">
                  {{ user.userName }}
                  <span class="mfa-recovery__badge">
                    {{ describeAuthority(user.authorityName) }}
                  </span>
                  <span
                    v-if="user.enrolled"
                    class="mfa-recovery__badge mfa-recovery__badge--ok"
                  >登録済み</span>
                  <span
                    v-else
                    class="mfa-recovery__badge mfa-recovery__badge--todo"
                  >未登録</span>
                </p>
                <p class="mfa-recovery__detail">
                  社員番号: {{ user.employeeId || '不明' }}
                  <template v-if="user.skippedCount > 0">
                    ／ 「あとで」を {{ user.skippedCount }} 回
                  </template>
                </p>
              </div>

              <!--
                登録していない人に「外す」ボタンを出さない。

                **押しても何も起きない。**外すものが無い。押せるボタンを
                出すと、押した人は「効かなかった」と思う。
              -->
              <CButton
                v-if="user.enrolled"
                color="outline-danger"
                size="sm"
                :disabled="busy"
                @click="startReset(user)"
              >2要素認証を外す</CButton>
            </li>
          </ul>
        </CCardBody>
      </CCard>
    </template>

    <!--
      外す前に確かめる。

      **取り返しがつかない。**外された人は、次のログインで登録を
      やり直すことになる。誰を外すのかを、押す前にもう一度見せる。
    -->
    <CModal
      :show="Boolean(resetTarget)"
      title="2要素認証を外します"
      color="danger"
      @update:show="cancelReset"
    >
      <p v-if="resetTarget">
        <b>{{ resetTarget.userName }}</b> さんの2要素認証を外します。
      </p>
      <p>
        外すと、この人は<b>次のログインで登録をやり直します</b>。
        いま持っている認証アプリの登録と予備のコードは、すべて使えなくなります。
      </p>
      <CInput
        v-model="resetReason"
        label="理由（記録に残ります）"
        placeholder="例: 端末を紛失、機種変更"
        maxlength="200"
      />
      <template #footer>
        <CButton color="secondary" :disabled="busy" @click="cancelReset">
          やめる
        </CButton>
        <CButton color="danger" :disabled="busy" @click="confirmReset">
          外す
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import {
  getEnrollmentStatus,
  resetMfaFor,
  serverMessage,
  isNetworkFailure,
  isReauthenticationRequired
} from '../../services/mfaAdminRecovery'

/**
 * 権限名の見せ方。
 *
 * **知らない値はそのまま出さない。** サーバが増やした値をそのまま出すと、
 * 英字のまま画面へ並ぶ。
 */
/**
 * いま何が起きているか。
 *
 * <b>サーバの段（AdminMfaStage）をそのまま出さない。</b>NONE / ANNOUNCED /
 * PROMPTED / ENFORCED と英字で並べても、開いた人には読めない。
 *
 * 見出しは<b>いま止まるかどうか</b>を先に言う。開いた人がいちばん
 * 知りたいのはそこで、段の名前ではない。
 */
const STAGE_LABELS = {
  NONE: {
    headline: 'いまは必須ではありません',
    detail: '管理者の2要素認証は求めていません。登録は本人まかせです。',
    tone: 'off'
  },
  ANNOUNCED: {
    headline: 'いまは必須ではありません（予告だけ出しています）',
    detail: '管理者のログイン後に案内が出ますが、まだ止まりません。',
    tone: 'soon'
  },
  PROMPTED: {
    headline: 'いまは必須ではありません（登録をお願いしています）',
    detail: '管理者のログイン後に登録画面が出ますが、「あとで」で先へ進めます。押せる回数には上限があります。',
    tone: 'soon'
  },
  ENFORCED: {
    headline: 'いま必須です',
    detail: '登録していない管理者は、登録するまで先へ進めません。',
    tone: 'on'
  }
}

const AUTHORITY_LABELS = {
  admin: '管理者',
  clerk: '担当者',
  common: '一般'
}

/*
 * **一時パスを出すボタンは、ここには置かない（場面 S-95）。**
 *
 * ここは顧客企業の管理者の画面。2要素を<b>外す</b>のはそれでよい
 * ——外された人は次のログインで自分で登録し直せる。
 *
 * 一時パスを<b>出す</b>のは違う。<b>会社側の管理者が、自分の第2要素を
 * 迂回する資格情報を自分で作れる</b>ことになる。出す画面は運営側
 * （views/platform/PlatformAccessManagement.vue）。
 */
export default {
  name: 'MfaRecovery',

  data () {
    return {
      status: {
        users: [], total: 0, notEnrolled: 0, adminsNotEnrolled: 0,
        stage: '', requiredFrom: null
      },
      loading: true,
      busy: false,
      message: '',
      errorMessage: '',
      resetTarget: null,
      resetReason: ''
    }
  },

  mounted () {
    this.reload()
  },

  computed: {
    /**
     * いまの段。
     *
     * <b>読めなかったときは空。</b>サーバは方針を読めなければ段を返さない。
     * そこを「必須ではありません」と言い換えると、**分からないことを
     * 大丈夫だと言ったことになる。**空なら、この札そのものを出さない。
     */
    stage () {
      return STAGE_LABELS[this.status.stage] || null
    },

    stageHeadline () {
      return this.stage ? this.stage.headline : ''
    },

    stageDetail () {
      if (!this.stage) return ''
      const from = this.status.requiredFrom
      if (this.status.stage === 'ENFORCED' || !from) {
        return this.stage.detail
      }
      return this.stage.detail
        + '必須になるのは ' + this.asDay(from) + ' からです。'
    },

    stageClass () {
      return this.stage
        ? 'mfa-recovery__stage--' + this.stage.tone
        : ''
    },

    /** 未登録のうち、管理者ではない人。**求められない人。** */
    othersNotEnrolled () {
      const all = Number(this.status.notEnrolled) || 0
      const admins = Number(this.status.adminsNotEnrolled) || 0
      return Math.max(0, all - admins)
    }
  },

  methods: {
    /** 日付だけにする。時刻は要らない。 */
    asDay (value) {
      const text = String(value || '')
      const day = text.slice(0, 10)
      return day.length === 10 ? day : text
    },

    async reload () {
      this.loading = true
      try {
        this.status = await getEnrollmentStatus()
      } catch (error) {
        this.status = { users: [], total: 0, notEnrolled: 0 }
        this.errorMessage = this.explain(
          error, '登録状況を読み込めませんでした。')
      } finally {
        this.loading = false
      }
    },

    startReset (user) {
      this.resetTarget = user
      this.resetReason = ''
      this.message = ''
      this.errorMessage = ''
    },

    cancelReset () {
      if (this.busy) return
      this.resetTarget = null
      this.resetReason = ''
    },

    async confirmReset () {
      if (this.busy || !this.resetTarget) return

      const target = this.resetTarget
      this.busy = true
      this.message = ''
      this.errorMessage = ''
      try {
        await resetMfaFor(target.userId, this.resetReason)
        this.resetTarget = null
        this.resetReason = ''
        this.message = target.userName
          + ' さんの2要素認証を外しました。'
          + ' 次のログインで登録をやり直してもらってください。'
        await this.reload()
      } catch (error) {
        this.errorMessage = this.explain(
          error, '2要素認証を外せませんでした。')
      } finally {
        this.busy = false
      }
    },

    /**
     * 断られた理由の見せ方。
     *
     * **サーバの文言をそのまま出す。** サーバは「自分で自分は外せません」
     * 「その利用者が見つかりません」を区別して返す。こちらで1種類に
     * まとめると、押した人は何を直せばよいか分からない。
     *
     * 本人確認のやり直しと通信断だけは、こちらで言い換える。
     * サーバの文言だけでは、次に何をすればよいか分からないため。
     */
    explain (error, fallback) {
      if (isReauthenticationRequired(error)) {
        return 'もう一度ログインの確認が必要です。'
          + ' 一度ログアウトして入り直してから、もう一度お試しください。'
      }
      if (isNetworkFailure(error)) {
        return '通信できませんでした。接続を確認してからお試しください。'
      }
      return serverMessage(error) || fallback
    },

    describeAuthority (authorityName) {
      return AUTHORITY_LABELS[authorityName] || 'その他'
    }
  }
}
</script>

<style scoped>
/*
  2026-08-27 まで、この画面には style が1行も無かった。
  クラス名だけ書いて、定義したつもりで入っていなかった。

  **書いた気になっていた形の典型。**利用者からは
  「なんかぐちゃぐちゃしてみにくい。外すボタンも何に対してかこれだと
  わからない」と言われた。名前と権限と状態が全部つながって1行になり、
  社員番号だけが別の行に並んでいた。
*/

.mfa-recovery__loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
}

/* ---- いま何が起きているか ---- */

.mfa-recovery__stage-card {
  border-left: 4px solid #d8dbe0;
}

.mfa-recovery__stage {
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.mfa-recovery__stage--off {
  color: #768192;
}

.mfa-recovery__stage--soon {
  color: #f9b115;
}

.mfa-recovery__stage--on {
  color: #2eb85c;
}

.mfa-recovery__stage-detail {
  margin: 0;
  color: #768192;
  font-size: 0.875rem;
}

/* ---- 数 ---- */

.mfa-recovery__counts {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  margin-bottom: 0.75rem;
}

.mfa-recovery__count {
  display: block;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  /* 数字の幅をそろえる。並べたときに桁がずれない。 */
  font-variant-numeric: tabular-nums;
}

.mfa-recovery__label {
  display: block;
  font-size: 0.8125rem;
  color: #768192;
}

.mfa-recovery__warn .mfa-recovery__count {
  color: #f9b115;
}

.mfa-recovery__note {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.7;
}

.mfa-recovery__aside {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.8125rem;
  color: #768192;
}

/* ---- 利用者ごとの状況 ---- */

.mfa-recovery__empty {
  margin: 0;
  color: #768192;
}

.mfa-recovery__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

/*
  **1人を1行にする。**ここが無かったので、25人ぶんの名前・権限・状態が
  切れ目なく1つの段落になっていた。「外す」ボタンが誰のものかも
  分からなくなる。
*/
.mfa-recovery__user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #ebedef;
}

.mfa-recovery__user:last-child {
  border-bottom: 0;
}

.mfa-recovery__body {
  min-width: 0;
}

.mfa-recovery__name {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0 0 0.125rem;
  font-weight: 600;
}

.mfa-recovery__detail {
  margin: 0;
  font-size: 0.8125rem;
  color: #768192;
}

.mfa-recovery__badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  background: #ebedef;
  color: #4f5d73;
  font-size: 0.75rem;
  font-weight: 600;
  /* 権限や状態は、名前と同じ太さで並ばせない。 */
  white-space: nowrap;
}

.mfa-recovery__badge--ok {
  background: #e6f6ec;
  color: #1b7a41;
}

.mfa-recovery__badge--todo {
  background: #fdf3e0;
  color: #8a5b00;
}

/*
  狭い画面では縦に積む。**ボタンを名前の下へ置く。**
  横に並べたままだと、名前が潰れて誰のボタンか読めなくなる。
*/
@media (max-width: 575.98px) {
  .mfa-recovery__user {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  .mfa-recovery__counts {
    gap: 1.25rem;
  }
}
</style>
